import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import {
  apiFetch,
  clearStoredAccessToken,
  getStoredAccessToken,
  refreshAccessToken,
  setStoredAccessToken,
} from './api';
import { UserSchema } from './schemas';
import { clearAllDrafts } from './Components/Common/applicationDraft';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Store user data or null if not authenticated
  const [token, setToken] = useState(null); // Store token for API requests
  const [isLoading, setIsLoading] = useState(true);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [signOutDestination, setSignOutDestination] = useState(null);
  const completeSignOutNavigation = useCallback(() => setSignOutDestination(null), []);

  useEffect(() => {
    const updateToken = (event) => setToken(event.detail);
    const syncStorage = (event) => {
      if (event.key === 'token') setToken(event.newValue);
    };
    window.addEventListener('auth:token-changed', updateToken);
    window.addEventListener('storage', syncStorage);
    return () => {
      window.removeEventListener('auth:token-changed', updateToken);
      window.removeEventListener('storage', syncStorage);
    };
  }, []);

  const loadUser = useCallback(async (accessToken, { skipRefresh = true } = {}) => {
    const response = await apiFetch('/user', {
      headers: { Authorization: `Bearer ${accessToken}` },
    }, { skipRefresh, onUnauthorized: () => {} });
    if (!response.ok) throw new Error('Failed to fetch user');
    const data = await response.json();
    const result = UserSchema.safeParse(data);
    if (!result.success) {
      console.error('User data validation failed:', result.error);
      throw new Error('Invalid user data received');
    }
    setUser(result.data);
    return result.data;
  }, []);

  const login = useCallback(async (accessToken) => {
    setToken(accessToken);
    setStoredAccessToken(accessToken);

    try {
      return await loadUser(accessToken);
    } catch (error) {
      setUser(null);
      setToken(null);
      clearStoredAccessToken();
      throw error;
    }
  }, [loadUser]);

  const logout = useCallback(async () => {
    try {
      await apiFetch('/logout', { method: 'POST' }, { skipAuth: true, skipRefresh: true, onUnauthorized: () => {} });
    } catch (error) {
      console.error('Error revoking session:', error);
    }
    setUser(null);
    setToken(null);
    clearStoredAccessToken();
  }, []);

  // A deliberate sign-out also wipes this browser's saved application drafts, so the next
  // person at a shared computer finds none. `logout` alone (an expired session) keeps
  // them, so the same applicant can sign back in and resume.
  const signOut = useCallback(async () => {
    setSignOutDestination(user?.user_type === 'public' ? '/public-login' : '/login');
    setIsSigningOut(true);
    clearAllDrafts();
    try {
      await logout();
    } finally {
      setIsSigningOut(false);
    }
  }, [logout, user?.user_type]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      try {
        const storedToken = getStoredAccessToken();
        if (storedToken) {
          setToken(storedToken);
          await loadUser(storedToken, { skipRefresh: false });
          if (!cancelled) {
            // apiFetch may have rotated an expired token while loading /user.
            setToken(getStoredAccessToken() || storedToken);
          }
          return;
        }

        const accessToken = await refreshAccessToken().catch(() => null);
        if (!accessToken) return;
        if (cancelled) return;
        setToken(accessToken);
        await loadUser(accessToken);
      } catch (error) {
        console.error('Error restoring user session:', error);
        clearStoredAccessToken();
        if (!cancelled) {
          setUser(null);
          setToken(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [loadUser]);


  return (
    <AuthContext.Provider value={{ user, token, isLoading, login, logout, signOut, isSigningOut, signOutDestination, completeSignOutNavigation }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
